import { ArrowCounterClockwiseIcon } from '@app/components/icons/arrow-counter-clockwise';
import { CheckmarkNewIcon } from '@app/components/icons/checkmark-new';
import { CloseNewIcon } from '@app/components/icons/close-new';
import { EditNewIcon } from '@app/components/icons/edit-new';
import { Scissors } from '@app/components/icons/scissors';
import { Undo } from '@app/components/icons/undo';
import { WordCorrection } from '@app/routes/content/routes/recordings/domain';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect, useRef, useState } from 'react';

import type { FlatWord } from './utils/build-flat-words';

// ── Types ─────────────────────────────────────────────────────────────────────

export type PopupPosition = {
  x: number;
  y: number;
};

type PopupMode =
  | { type: 'word-buttons' } // single normal word, not corrected
  | { type: 'corrected-buttons' } // single corrected word
  | { type: 'multi-buttons' } // 2+ normal words selected
  | { type: 'gap-button' } // a deleted gap was clicked
  | { type: 'edit'; value: string }; // inline text-edit mode

type WordActionPopupProps = {
  /** Non-cut words currently selected (empty when gap selection is active). */
  selectedWords: FlatWord[];
  /** Cut words in the clicked gap (empty when normal selection is active). */
  cutGapWords: FlatWord[];
  /** Viewport coordinates near the last selected word. */
  position: PopupPosition;
  /** Callbacks */
  onRemoveFromVideo: () => void;
  onRestoreToVideo: () => void;
  onSaveCorrection: (newCorrections: WordCorrection[]) => void;
  onClose: () => void;
  /** Context needed for correction save logic. */
  wordCorrections: WordCorrection[];
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const POPUP_HEIGHT_ESTIMATE = 52; // px — used to decide above/below
const POPUP_WIDTH_ESTIMATE = 280; // px — used for horizontal clamping
const POPUP_GAP = 8; // px gap between word and popup

const getInitialMode = (
  selectedWords: FlatWord[],
  cutGapWords: FlatWord[],
): PopupMode => {
  if (cutGapWords.length > 0) return { type: 'gap-button' };
  if (selectedWords.length > 1) return { type: 'multi-buttons' };
  if (selectedWords.length === 1) {
    return selectedWords[0].correction !== undefined
      ? { type: 'corrected-buttons' }
      : { type: 'word-buttons' };
  }
  return { type: 'word-buttons' }; // fallback
};

// ── Shared button styles ──────────────────────────────────────────────────────

const BTN =
  'flex items-center justify-center gap-1.5 rounded-[4px] font-medium h-5 border-1 border-spring-neutral-b0/20 text-xxs' +
  'bg-spring-neutral-base text-white hover:bg-spring-neutral-b0/10 transition-colors whitespace-nowrap px-2';

const CONFIRM_BTN =
  'flex items-center justify-center w-8 h-8 rounded-[4px] text-green-400 ' +
  'hover:bg-spring-neutral-b0/10 transition-colors';

const ICON = 'w-3 h-3 shrink-0 text-white';

const ICON_BTN = BTN + ' border-none!';

// ── Sub-components ────────────────────────────────────────────────────────────

const CloseButton = ({ onClose }: { onClose: () => void }) => (
  <button
    type="button"
    className={ICON_BTN}
    onClick={onClose}
    aria-label="Close"
  >
    <CloseNewIcon className={ICON} />
  </button>
);

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Unified floating action popup for the EditTranscript component.
 *
 * Manages 5 internal states:
 *   - word-buttons      single non-corrected word
 *   - corrected-buttons single corrected word
 *   - multi-buttons     2+ words selected
 *   - gap-button        a deleted gap clicked
 *   - edit              inline text-edit mode (replaces InlineWordEditor)
 *
 * Positioned via `position: fixed` so it escapes any scroll containers.
 */
export const WordActionPopup = ({
  selectedWords,
  cutGapWords,
  position,
  onRemoveFromVideo,
  onRestoreToVideo,
  onSaveCorrection,
  onClose,
  wordCorrections,
}: WordActionPopupProps) => {
  const { t } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);

  const [mode, setMode] = useState<PopupMode>(() =>
    getInitialMode(selectedWords, cutGapWords),
  );

  // ── Input ref for edit mode auto-focus ──────────────────────────────────
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (mode.type === 'edit' && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [mode.type]);

  // ── Global keyboard handler ──────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ── Close on outside click ───────────────────────────────────────────────
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // ── Correction save logic ────────────────────────────────────────────────
  const commitCorrection = (newText: string) => {
    const fw = selectedWords[0];
    if (!fw) return;

    const { segmentIndex, wordIndex } = fw;
    const trimmed = newText.trim();

    let newCorrections: WordCorrection[];

    if (trimmed === '' || trimmed === fw.word.word) {
      // Remove any existing correction
      newCorrections = wordCorrections.filter(
        wc => !(wc.segmentIndex === segmentIndex && wc.wordIndex === wordIndex),
      );
    } else {
      const existingIdx = wordCorrections.findIndex(
        wc => wc.segmentIndex === segmentIndex && wc.wordIndex === wordIndex,
      );
      if (existingIdx >= 0) {
        newCorrections = wordCorrections.map((wc, i) =>
          i === existingIdx ? { ...wc, word: trimmed } : wc,
        );
      } else {
        newCorrections = [
          ...wordCorrections,
          { segmentIndex, wordIndex, word: trimmed },
        ];
      }
    }

    onSaveCorrection(newCorrections);
    onClose();
  };

  // ── Positioning ──────────────────────────────────────────────────────────
  // position.y = rect.bottom of the last highlighted word.
  // Default: show below the word. Flip above only if there is not enough
  // space between the word's bottom and the viewport bottom.
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const showAbove = position.y + POPUP_GAP + POPUP_HEIGHT_ESTIMATE > vh;

  // Horizontal: centre on the anchor word, then clamp to viewport edges.
  const halfW = POPUP_WIDTH_ESTIMATE / 2;
  const clampedX = Math.min(Math.max(position.x, halfW + 8), vw - halfW - 8);

  const popupStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 50,
    left: clampedX,
    transform: 'translateX(-50%)',
    ...(showAbove
      ? { top: position.y - POPUP_HEIGHT_ESTIMATE - POPUP_GAP }
      : { top: position.y + POPUP_GAP }),
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      ref={popupRef}
      style={popupStyle}
      className="bg-black rounded-lg shadow-lg border h-9 px-2 flex items-center gap-1 border-none"
      role="menu"
      tabIndex={-1}
      aria-label="Word actions"
      // Prevent popup's own mousedown from triggering outside-click handler
      onMouseDown={e => e.stopPropagation()}
    >
      {mode.type === 'word-buttons' && (
        <>
          <button
            type="button"
            className={BTN}
            role="menuitem"
            onClick={() =>
              setMode({
                type: 'edit',
                value:
                  selectedWords[0]?.correction ??
                  selectedWords[0]?.word.word ??
                  '',
              })
            }
          >
            <EditNewIcon className={ICON} />
            {t('content-recording.editor-v2.fix-transcript')}
          </button>

          <button
            type="button"
            className={BTN}
            role="menuitem"
            onClick={() => {
              onRemoveFromVideo();
              onClose();
            }}
          >
            <Scissors className={ICON} />
            {t('content-recording.editor-v2.remove-from-video')}
          </button>

          <CloseButton onClose={onClose} />
        </>
      )}

      {mode.type === 'corrected-buttons' && (
        <>
          <button
            type="button"
            className={BTN}
            role="menuitem"
            onClick={() => {
              const fw = selectedWords[0];
              if (!fw) return;
              const newCorrections = wordCorrections.filter(
                wc =>
                  !(
                    wc.segmentIndex === fw.segmentIndex &&
                    wc.wordIndex === fw.wordIndex
                  ),
              );
              onSaveCorrection(newCorrections);
              onClose();
            }}
          >
            <Undo className={ICON} />
            {t('content-recording.editor-v2.revert-change')}
          </button>

          <button
            type="button"
            className={BTN}
            role="menuitem"
            onClick={() =>
              setMode({
                type: 'edit',
                value:
                  selectedWords[0]?.correction ??
                  selectedWords[0]?.word.word ??
                  '',
              })
            }
          >
            <EditNewIcon className={ICON} />
            {t('content-recording.editor-v2.fix-transcript')}
          </button>

          <CloseButton onClose={onClose} />
        </>
      )}

      {mode.type === 'multi-buttons' && (
        <>
          <button
            type="button"
            className={BTN}
            role="menuitem"
            onClick={() => {
              onRemoveFromVideo();
              onClose();
            }}
          >
            <Scissors className={ICON} />
            {t('content-recording.editor-v2.remove-from-video')}
          </button>

          <CloseButton onClose={onClose} />
        </>
      )}

      {mode.type === 'gap-button' && (
        <>
          <button
            type="button"
            className={BTN}
            role="menuitem"
            onClick={() => {
              onRestoreToVideo();
              onClose();
            }}
          >
            <ArrowCounterClockwiseIcon className={ICON} />
            {t('content-recording.editor-v2.restore-to-video')}
          </button>

          <CloseButton onClose={onClose} />
        </>
      )}

      {mode.type === 'edit' && (
        <>
          <input
            ref={inputRef}
            type="text"
            defaultValue={mode.value}
            className="bg-spring-neutral-b0/20 text-white text-sm rounded-[4px] outline-none focus:ring-1 focus:ring-spring-primary min-w-[120px] h-5"
            aria-label={t('content-recording.editor-v2.fix-transcript')}
            onMouseDown={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitCorrection(e.currentTarget.value);
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
              }
            }}
          />
          <button
            type="button"
            className={CONFIRM_BTN}
            aria-label="Confirm"
            onClick={e => {
              const input = (e.currentTarget as HTMLButtonElement)
                .previousElementSibling as HTMLInputElement | null;
              if (input) commitCorrection(input.value);
            }}
          >
            <CheckmarkNewIcon className={ICON} />
          </button>
        </>
      )}
    </div>
  );
};
